import React, { useId, useState } from 'react';
import { Box, CSSObject, DialogProps, IconButton, styled, SxProps, Theme, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { TextboxInput } from '../../components/atoms';
import { PopUpModal } from '../../components/molecules/PopUpModal/PopUpModal';
import { ReactComponent as InfoIcon } from '../../assets/icons/info-alert.svg';
import { ReactComponent as CloseIcon } from '../../assets/icons/x-mark-dialog.svg';
import { FTDS_MODAL_SHADOW } from '../../themes/styles/common';
import { formatCurrency, removeNonNumeric } from '../../utils';

// AEX-3724: Figma (GOE DA - Banorte) tokens shared by both steps of the Update Funding & Goals flow.
const FONT_FAMILY = '"TT Commons Pro"';
// Figma text styles have no letter spacing, while MUI typography (tables, buttons, inputs) adds some.
const BASE_TEXT: React.CSSProperties = { fontFamily: FONT_FAMILY, letterSpacing: 0 };

export const getBodyTextSx = (theme: Theme): React.CSSProperties => ({
    ...BASE_TEXT,
    fontWeight: 400,
    fontSize: '16px',
    lineHeight: '22px',
    fontFeatureSettings: '"liga" 0',
    color: theme.palette.semantic.text.default
});

export const labelTextSx: React.CSSProperties = {
    ...BASE_TEXT,
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '19px'
};

export const TABLE_ROW_HEIGHT = '40px';
// The table hard-codes a 51px header cell height through a 3-class selector, so a plain/`&&` override
// can't outrank it; !important makes the Figma 40px header deterministic.
export const tableHeaderCellSx: SxProps<Theme> = {
    letterSpacing: 0,
    padding: '8px',
    verticalAlign: 'middle',
    height: `${TABLE_ROW_HEIGHT} !important`
};
// Figma top-aligns the body cells; the amount cells sit 8px from the top instead of 12px.
export const tableBodyCellSx = { letterSpacing: 0, verticalAlign: 'top', px: 1, py: 1.5 };
export const amountCellSx = { ...tableBodyCellSx, py: 1 };

// Figma's 2px secondary-button stroke sits inside the button, so it comes out of the padding and both
// variants keep the same 38px box.
export const stepButtonSx: SxProps<Theme> = {
    ...BASE_TEXT,
    height: '38px',
    py: 1,
    px: 2,
    fontWeight: 400,
    lineHeight: '22px',
    '&.MuiButton-outlined, &.MuiButton-outlined:hover': { borderWidth: '2px', py: 0.75, px: 1.75 }
};

// Figma draws strokes inside a layer without changing its size; an overlaid inset shadow does the same,
// so the banner and tables keep Figma's exact dimensions (a CSS border would add to them).
const insideStroke = (color: string): CSSObject => ({
    position: 'relative',
    '&::after': {
        content: '""',
        position: 'absolute',
        inset: 0,
        borderRadius: 'inherit',
        boxShadow: `inset 0 0 0 1px ${color}`,
        pointerEvents: 'none'
    }
});

// Rounded table frame with Figma's grid: one 1px line on the right and bottom of every cell, drawn inside the
// cell like the frame's own stroke. The 4-class selector beats the table's border rules (up to 3 classes).
export const TableFrame = styled(Box)(({ theme }) => ({
    ...insideStroke(theme.palette.semantic.bd.subtle),
    borderRadius: theme.spacing(1),
    overflow: 'hidden',
    '& .MuiTable-root .MuiTableRow-root .MuiTableCell-root': {
        border: 'none',
        boxShadow: `inset -1px -1px 0 ${theme.palette.semantic.bd.subtle}`
    }
}));

const InfoBox = styled(Box)(({ theme }) => ({
    ...insideStroke(theme.palette.semantic.bd.info),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(1, 2),
    borderRadius: theme.spacing(0.75),
    backgroundColor: theme.palette.semantic.bg.info,
    '& svg': { flexShrink: 0 },
    '& svg path': { fill: theme.palette.semantic.icon.info }
}));

export const InfoBanner: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const theme = useTheme();
    return (
        <InfoBox>
            <InfoIcon aria-hidden />
            <Box component="span" sx={getBodyTextSx(theme)}>
                {children}
            </Box>
        </InfoBox>
    );
};

// The theme draws input outlines as a border on the input root (its notched outline is hidden).
const getAmountInputSx = (theme: Theme): SxProps<Theme> => ({
    width: theme.spacing(21),
    marginLeft: 'auto',
    '& .MuiInputBase-root': {
        height: theme.spacing(4),
        padding: theme.spacing(0, 2),
        borderRadius: theme.spacing(0.5),
        borderColor: theme.palette.neutral[500],
        '&:hover': { borderColor: theme.palette.neutral[500] }
    },
    '& .MuiInputBase-input': { ...getBodyTextSx(theme), height: '22px', padding: 0, textAlign: 'right' }
});

type AmountInputProps = {
    value: number | string;
    // eslint-disable-next-line no-unused-vars
    onChange: (amount: number) => void;
};

// Figma shows amounts with cents; while focused the field keeps the app's whole-number editing format.
export const AmountInput: React.FC<AmountInputProps> = ({ value, onChange }) => {
    const theme = useTheme();
    const [amount, setAmount] = useState(() => Number(removeNonNumeric(value)));
    const [isFocused, setIsFocused] = useState(false);

    return (
        <TextboxInput
            value={formatCurrency(amount, isFocused)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            onChange={(event) => {
                const nextAmount = Number(removeNonNumeric(event.target.value));
                setAmount(nextAmount);
                onChange(nextAmount);
            }}
            size="small"
            type="text"
            sx={getAmountInputSx(theme)}
        />
    );
};

type UpdateFundingGoalsStepModalProps = {
    open: boolean;
    title: string;
    subTitle: string;
    onClose: () => void;
    // Right-aligned footer buttons; the Cancel link is rendered by the frame.
    actions: React.ReactNode;
    children: React.ReactNode;
    transitionDuration?: DialogProps['transitionDuration'];
};

const UpdateFundingGoalsStepModal: React.FC<UpdateFundingGoalsStepModalProps> = ({
    open,
    title,
    subTitle,
    onClose,
    actions,
    children,
    transitionDuration
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const bodyText = getBodyTextSx(theme);
    // Per-instance ids: both step dialogs briefly coexist during the instant swap, so the fixed
    // PopUpModal ids would be duplicated and the new dialog could be announced with the old title.
    const titleId = useId();
    const subTitleId = useId();

    return (
        <PopUpModal
            open={open}
            title={title}
            showTitle={false}
            showCancelButton={false}
            width="1038px"
            height="600px"
            transitionDuration={transitionDuration}
            PaperProps={{ 'aria-labelledby': titleId, 'aria-describedby': subTitleId }}
            handleClose={onClose}
            handleSaveButtonAction={onClose}
            sx={{
                '& .MuiDialog-paper': {
                    borderRadius: theme.spacing(1.5),
                    boxShadow: `${FTDS_MODAL_SHADOW} !important`
                },
                '& .MuiDialogContent-root': { padding: '32px', borderTop: 'none', borderBottom: 'none' },
                '& .MuiDialogActions-root': { padding: '0 32px 32px' }
            }}
            description={
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px', width: '100%' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                            <Box
                                component="h2"
                                id={titleId}
                                sx={{
                                    flex: '1 1 0',
                                    margin: 0,
                                    ...BASE_TEXT,
                                    fontWeight: 700,
                                    fontSize: '24px',
                                    lineHeight: '30px',
                                    color: theme.palette.primary[800]
                                }}
                            >
                                {title}
                            </Box>
                            <IconButton
                                aria-label={t('CLOSE_LABEL')}
                                onClick={onClose}
                                sx={{
                                    width: theme.spacing(4),
                                    height: theme.spacing(4),
                                    padding: 0,
                                    '& svg path': { fill: theme.palette.semantic.int.selected }
                                }}
                            >
                                <CloseIcon aria-hidden />
                            </IconButton>
                        </Box>
                        <Box component="p" id={subTitleId} sx={{ margin: 0, ...bodyText }}>
                            {subTitle}
                        </Box>
                    </Box>
                    {children}
                </Box>
            }
            footerComp={
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: '16px' }}>
                    <Box sx={{ flex: '1 1 0' }}>
                        <Box
                            component="button"
                            type="button"
                            onClick={onClose}
                            sx={{
                                background: 'transparent',
                                border: 'none',
                                padding: 0,
                                cursor: 'pointer',
                                ...bodyText,
                                whiteSpace: 'nowrap',
                                color: theme.palette.semantic.int.selected,
                                textDecorationLine: 'underline',
                                textDecorationSkipInk: 'none',
                                textUnderlinePosition: 'from-font'
                            }}
                        >
                            {t('BUTTON_CANCEL')}
                        </Box>
                    </Box>
                    {actions}
                </Box>
            }
        />
    );
};

export default UpdateFundingGoalsStepModal;
